import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Spinner, Alert, Btn, PageHeader, Breadcrumb, SectionLabel } from '../../components/UI';
import { ArrowTopRightOnSquareIcon, LockClosedIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { api } from '../../utils/api';
import { Needs, Design, ProposalScreen, setWorkspaceContext } from '../master/MasterPages';
import DeliveryDays from './DeliveryDays';
import WayOfSelling, { StatusChip, FlagChips } from './WayOfSelling';
import { useMobile } from '../../components/Layout';
import { LatestFacts } from './ClientsTool';

// One Workplace, One Shelf (W2/W3): the customer workspace.
// Verbs live with the customer — the journey is the left menu (stages under phase
// headers, 🔒 gates between them), the selected stage's step renders on the right,
// role tracks as tabs above. Needs/Design/Proposal are the team's engagement screens,
// embedded here (routing + embedding only — the data model and gates are unchanged).

// Phase metadata by CRM stage anchor (E/M/D on the sales process stages).
const PHASE = {
  E: { name: 'EXPLORE', tagline: 'promote & prospect', shelf: '/explore', label: 'Explore',
    howTo: 'Find and understand. Decide who is worth your time, check who the network already knows (recommendation first), and open a project when the conversation is real. Then gather what the customer’s people tell you into a Needs Summary in their own words — when they confirm it, that is Gate 1.' },
  M: { name: 'MASTER', tagline: 'sell & adapt', shelf: '/master', label: 'Master',
    howTo: 'Design and agree. Turn the confirmed need into a programme — sharper with an HB Compass profile, or directly by choosing the Units you know will work; both are correct. Confirming the design is Gate 2. The proposal is built from that design; the decision maker accepting it is Gate 3, the signed agreement Gate 4. If it stops, record the outcome — lost with its reason, parked or unresponsive; won, the client is proposed for the shared registry and enters only with your approval.' },
  D: { name: 'DELIVER', tagline: 'excellence in the room', shelf: '/deliver', label: 'Deliver',
    howTo: 'In the room. Prepare from the Unit records — running order, timing, materials, and who has mastered it, so you can ask. The delivery itself is yours and is not written down. Afterwards keep what lasts: the log, the materials used, and any adaptation you made — approved, it goes back on the shelf with your name on it.' },
};
// Guidance copy follows "The Model Behind Basecamp" use cases (Sept 2026, draft) — edit freely; the steps, tools and gates below come from the
// sales process itself (Process Designer), so they follow any change made there.

// The gates sit between menu items — by the model ("The Model Behind Basecamp"):
// Prospect · Needs |G1| Design |G2| Proposal |G3| Closing · Delivering · Completed.
// The gate is drawn BEFORE the stage it opens (key = that stage's Seq, so renaming never breaks it).
// HB Way of Selling v1.1 (a process with steps): Gate 3 is the proposal ACCEPTED, and Gates 4–5 lead
// into Delivering and Completed.
const GATE_BEFORE_V1 = {
  3: 'GATE 1 · need confirmed',
  4: 'GATE 2 · design confirmed',
  5: 'GATE 3 · proposal sent',
};
const GATE_BEFORE_V11 = {
  3: 'GATE 1 · need confirmed',
  4: 'GATE 2 · design confirmed',
  5: 'GATE 3 · proposal accepted',
  6: 'GATE 4 · agreement signed',
  7: 'GATE 5 · results presented',
};

// Which engagement screen renders for a stage, by stage name.
function stepFor(stageName) {
  const n = (stageName || '').toLowerCase();
  if (/needs/.test(n)) return 'needs';
  if (/design|profile|ideal/.test(n)) return 'design';
  if (/proposal|programme/.test(n)) return 'proposal';
  return null;
}

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

export default function CustomerWorkspace({ id }) {
  const projectId = parseInt(id, 10);
  const navigate = useNavigate();
  const [eng, setEng] = useState(null);
  const [rail, setRail] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [error, setError] = useState(null);
  const [stageIdx, setStageIdx] = useState(null);
  const [trackId, setTrackId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [railRev, setRailRev] = useState(0); // bumps when fresh rail data lands
  const [busy, setBusy] = useState(false);
  const topRef = useRef(null);
  // Phones: the journey menu folds into one line above the step, so the work is not pushed below it.
  const isMobile = useMobile();
  const [menuOpen, setMenuOpen] = useState(false);

  const loadTracks = useCallback(() => {
    api.master.getRoleTracks(projectId)
      .then(d => setTracks(d.roleTracks || []))
      .catch(() => setTracks([]));
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    setError(null);
    Promise.all([
      api.master.getEngagement(projectId).catch(() => null),
      api.explore.getRail(projectId).catch(() => ({ stages: [] })),
      api.master.getRoleTracks(projectId).catch(() => ({ roleTracks: [] })),
    ]).then(([e, r, t]) => {
      if (!alive) return;
      setEng(e);
      setRail(r);
      setRailRev(v => v + 1);
      const trs = t.roleTracks || [];
      setTracks(trs);
      if (trs.length) setTrackId(prev => prev ?? trs[0].id);
      // Land on the current stage.
      const stages = r.stages || [];
      const curIdx = Math.max(0, stages.findIndex(s => s.id === r.currentStageId));
      setStageIdx(prev => (prev == null ? curIdx : prev));
    }).catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [projectId, reloadKey]);

  // The embedded Needs / Design / Proposal screens announce gate and proposal changes, which
  // can move the stage — reload the rail and the Way of Selling panel (the step stays mounted).
  useEffect(() => {
    const onChanged = () => setReloadKey(k => k + 1);
    window.addEventListener('hb-engagement-changed', onChanged);
    return () => window.removeEventListener('hb-engagement-changed', onChanged);
  }, []);

  // "Continue to Design / Proposal →" inside an embedded step stays in the workspace.
  const stageNames = (rail?.stages || []).map(s => s.name).join('|');
  useEffect(() => {
    const onGo = (e) => {
      const i = stageNames.split('|').findIndex(n => stepFor(n) === e.detail?.step);
      if (i < 0) return;
      e.preventDefault();
      setStageIdx(i);
      topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener('hb-workspace-go', onGo);
    return () => window.removeEventListener('hb-workspace-go', onGo);
  }, [stageNames]);

  if (error) return <Alert type="error">{error}</Alert>;
  if (!rail || stageIdx == null) return <Loading />;

  const stages = rail.stages || [];
  if (!stages.length) {
    return (
      <>
        <Breadcrumb items={[{ label: 'My Pipeline', to: '/customers/journeys' }, { label: eng?.project?.name || `#${projectId}` }]} />
        <Alert type="warn">There is no active sales process to place this customer on. An administrator activates one in the <Link to="/admin/process" style={{ textDecoration: 'underline' }}>Sales Process Designer</Link>.</Alert>
      </>
    );
  }

  const wos = rail.wayOfSelling || null; // HB Way of Selling v1.1 (null on v1)
  const GATE_BEFORE = wos ? GATE_BEFORE_V11 : GATE_BEFORE_V1;
  const seqAt = (i) => (stages[i] ? stages[i].seq ?? i + 1 : null);
  const project = eng?.project || {};
  const custName = project.customer || project.companyName || '';
  const projName = project.name || `Customer #${projectId}`;
  const curStageIdx = Math.max(0, stages.findIndex(s => s.id === rail.currentStageId));
  const sel = stages[stageIdx] || stages[0];
  const selStep = stepFor(sel.name);
  const selAnchor = sel.anchor || 'M';

  // HB Compass is a per-track flag (W6). No track yet → default on. Locked after Gate 2.
  const selTrack = tracks.find(t => t.id === trackId);
  const compass = selTrack ? selTrack.useCompass !== false : true;
  const compassLocked = (seqAt(curStageIdx) ?? 0) >= 4; // past Gate 2 (Proposal onwards)

  // Drive the embedded screens: set the shared context BEFORE they mount, then remount
  // via `ctxKey` so they read the fresh project/track (and the Compass switch, which picks
  // between assembling a profile and choosing Units directly in Design).
  setWorkspaceContext(projectId, trackId);
  const ctxKey = `${projectId}:${trackId || 'none'}:${stageIdx}:${compass ? 'c' : 'u'}`;

  async function addTrack() {
    // eslint-disable-next-line no-alert
    const roleName = window.prompt('New role track — role / population (e.g. "Sales Team"):');
    if (!roleName || !roleName.trim()) return;
    try {
      const r = await api.master.createRoleTrack(projectId, { roleName: roleName.trim(), level: '', population: '' });
      loadTracks();
      setTrackId(r.roleTrackId);
      setReloadKey(k => k + 1); // opening a track moves the project to Needs
    } catch (e) { alert(e.message); }
  }

  async function toggleCompass() {
    if (!selTrack) { alert('Open a role track first — HB Compass is set per track.'); return; }
    if (compassLocked) {
      alert('HB Compass can be switched only until Gate 2 — this customer is past it.');
      return;
    }
    try {
      await api.master.setCompass(selTrack.id, !compass);
      loadTracks();
    } catch (e) { alert(e.message); }
  }

  // ── Journey menu (left) ──────────────────────────────────────────────────
  // App-style: phase names are the shelf links (with a ↗ icon), subtle gates, status dots.
  const menu = [];
  let prevAnchor = null;
  const seenAnchors = new Set();
  stages.forEach((s, i) => {
    const anchor = s.anchor || 'M';
    if (anchor !== prevAnchor && PHASE[anchor]) {
      const ph = PHASE[anchor];
      const again = seenAnchors.has(anchor);
      seenAnchors.add(anchor);
      menu.push(
        <button key={`ph-${i}`} onClick={() => navigate(ph.shelf)} style={phHeadStyle} title={`Open the ${ph.label} shelf`}>
          <span>{again ? `↺ back to ${ph.name}` : ph.name}</span>
          <ArrowTopRightOnSquareIcon style={{ width: 12, height: 12, opacity: 0.55 }} />
        </button>
      );
      prevAnchor = anchor;
    }
    if (GATE_BEFORE[seqAt(i)]) menu.push(
      <div key={`gate-${i}`} style={gateStyle}>
        <LockClosedIcon style={{ width: 11, height: 11, flexShrink: 0 }} />
        <span>{GATE_BEFORE[seqAt(i)]}</span>
      </div>
    );
    const isCur = i === curStageIdx;
    const isDone = i < curStageIdx;
    const isSel = i === stageIdx;
    const isDesign = stepFor(s.name) === 'design';
    const opt = isDesign ? (compass ? 'with HB Compass profile' : 'Units chosen directly') : (isCur ? 'current stage' : '');
    menu.push(
      <button
        key={`st-${i}`}
        onClick={() => { setStageIdx(i); setMenuOpen(false); topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
        style={{
          ...navItemStyle,
          background: isSel ? 'var(--ink)' : 'transparent',
          color: isSel ? '#fff' : (isDone ? 'var(--ink)' : 'var(--ink-soft)'),
          fontWeight: isSel ? 600 : 400,
        }}
      >
        <StageDot done={isDone} current={isCur} selected={isSel} />
        <span style={{ flex: 1, minWidth: 0 }}>
          {s.name}
          {opt && <small style={{ display: 'block', fontSize: '0.64rem', color: isSel ? 'rgba(255,255,255,0.6)' : 'var(--ink-faint)' }}>{opt}</small>}
        </span>
      </button>
    );
  });

  // ── Step (right) ─────────────────────────────────────────────────────────
  const isProfileStage = selStep === 'design';
  const refresh = () => { loadTracks(); setReloadKey(k => k + 1); };
  async function act(fn) {
    setBusy(true);
    try { await fn(); refresh(); } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function markLost() {
    const reasons = rail.lostReasons || [];
    const list = reasons.map((r, i) => `${i + 1}. ${r.label}`).join('\n');
    // eslint-disable-next-line no-alert
    const pick = window.prompt(`Mark this project lost — the reason matters more to the network than the win.\n${list}\n\nEnter number:`);
    const i = parseInt(pick, 10);
    if (i >= 1 && i <= reasons.length) act(() => api.explore.markLost(projectId, { reasonId: reasons[i - 1].id }));
  }
  const seq = sel.seq ?? stageIdx + 1;
  const confirmStep = (code, note) => act(() => api.explore.setStep(projectId, code, { confirmed: true, note }));
  const stepDone = (code) => !!(wos && (wos.steps.find(x => x.code === code) || {}).confirmed);
  const isCurrent = stageIdx === curStageIdx;

  let step;
  if (selStep === 'needs') {
    step = <Needs key={ctxKey} />;
  } else if (selStep === 'design') {
    step = <Design key={ctxKey} />;
  } else if (selStep === 'proposal') {
    step = <ProposalScreen key={ctxKey} />;
  } else {
    // Stages without an engagement screen: the event that moves the project on lives here.
    let action = null;
    if (wos && seq === 0) {
      action = <>
        <p style={stepNoteStyle}>Selected — the first conversation is still ahead. Schedule the first meeting and the needs conversation; when the first meeting is held, confirm it and the project becomes a Prospect.</p>
        <Btn size="sm" loading={busy} onClick={() => confirmStep('prospect')}>First meeting held</Btn>
      </>;
    } else if (seq === 1) {
      action = tracks.length
        ? <p style={stepNoteStyle}>A role track is open — the project is in Needs. Continue there.</p>
        : <><p style={stepNoteStyle}>When the conversation is real, start discovering the need: open a role track for the population you’ll work with (e.g. “Sales Team”). That moves the project to Needs.</p>
            <Btn size="sm" onClick={addTrack}>+ Open a role track</Btn></>;
    } else if (wos && seq === 5 && isCurrent && !wos.outcome.outcome) {
      action = <>
        <p style={stepNoteStyle}>The decision maker accepted the proposal in principle. Agree the terms — when the agreement is signed or the order received, confirm Gate 4: the opportunity is won. If it stops, record the outcome below.</p>
        <Btn size="sm" loading={busy} onClick={() => confirmStep('gate4', 'Agreement signed')}>Agreement signed — Gate 4</Btn>
      </>;
    } else if (seq === 5 && isCurrent && !rail.isLost) {
      action = <>
        <p style={stepNoteStyle}>The proposal is with the customer. Record the outcome — won or lost, the funnel updates itself.</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn size="sm" loading={busy} onClick={() => act(() => api.explore.updateProject(projectId, { closedWon: true }))}>Agreement reached — won</Btn>
          <Btn size="sm" variant="outline" onClick={markLost}>Lost — record the reason</Btn>
        </div>
      </>;
    } else if (seq === 6 && rail.wonAt) {
      action = <>
        <p style={stepNoteStyle}>Prepare from the Unit records — running order, timing, materials, and who has mastered it. The room is yours. After each day keep what lasts: the log, the materials used, any adaptation.</p>
        <DeliveryDays projectId={projectId} delivered={!!rail.deliveredAt} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <Btn size="sm" variant={wos ? 'outline' : undefined} loading={busy} onClick={() => act(() => api.explore.markDelivered(projectId, !!rail.deliveredAt))}>{rail.deliveredAt ? 'Undo “programme delivered”' : 'Programme delivered'}</Btn>
          {wos && !stepDone('gate5') && <Btn size="sm" loading={busy} onClick={() => confirmStep('gate5', 'Results presented')}>Results presented to the sponsor — Gate 5</Btn>}
        </div>
      </>;
    } else if (seq === 7) {
      action = <>
        <p style={stepNoteStyle}>Delivered. The journey loops back to Explore — what does this customer need next?</p>
        <Btn size="sm" variant="outline" onClick={() => navigate('/customers/journeys')}>+ New project for {custName || 'this customer'}</Btn>
      </>;
    }
    step = (
      <div>
        <h2 style={stepTitleStyle}>{sel.name}</h2>
        {wos?.outcome?.outcome && isCurrent
          ? <Alert type="warn">This project is marked <b>{wos.outcome.outcome}</b> — the outcome is recorded below.</Alert>
          : rail.isLost && isCurrent && <Alert type="warn">This project is marked <b>lost</b>.</Alert>}
        {action || (
          <p style={{ fontSize: '0.9rem', color: 'var(--ink-soft)', lineHeight: 1.6 }}>
            {sel.entryCriterion || 'The knowledge, method and materials for this phase live on the shelf.'}
          </p>
        )}
      </div>
    );
  }

  const ph = PHASE[selAnchor] || PHASE.M;
  const nextStage = stages[stageIdx + 1];
  const milestones = sel.milestones || [];
  const kit = sel.kit || [];

  const embedded = selStep === 'needs' || selStep === 'design' || selStep === 'proposal';

  return (
    <>
      <div ref={topRef} style={{ scrollMarginTop: 24 }} />
      <Breadcrumb items={[{ label: 'My Pipeline', to: '/customers/journeys' }, { label: custName || projName }]} />
      <PageHeader
        title={projName}
        subtitle={<>{custName ? `${custName} · ` : ''}the stage moves only through the gates{wos && <span style={{ display: 'inline-flex', gap: 6, marginLeft: 10, verticalAlign: 'middle' }}><StatusChip status={wos.status} /><FlagChips flags={wos.flags} /></span>}</>}
      />

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Journey menu — same left-column pattern as the rest of the app (AreaLayout) */}
        {isMobile ? (
          <div style={{ width: '100%' }}>
            <button onClick={() => setMenuOpen(o => !o)} aria-expanded={menuOpen} style={journeyToggleStyle}>
              <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <span style={{ ...howToLabelStyle, display: 'block' }}>{ph.label} · step {stageIdx + 1} of {stages.length}</span>
                <b style={{ fontSize: '0.92rem' }}>{sel.name}</b>
              </span>
              <span aria-hidden style={{ fontSize: '0.8rem', color: 'var(--ink-soft)' }}>{menuOpen ? '▲' : '▼'}</span>
            </button>
            {menuOpen && <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6, background: 'var(--canvas-white)', border: 'var(--panel-border)', borderRadius: 'var(--radius-panel)', padding: 8 }}>{menu}</div>}
          </div>
        ) : (
          <div style={{ width: 210, flexShrink: 0, position: 'sticky', top: 36, display: 'flex', flexDirection: 'column', gap: 2, background: 'var(--canvas-white)', border: 'var(--panel-border)', borderRadius: 'var(--radius-panel)', padding: 8 }}>{menu}</div>
        )}

        {/* Right: role tracks → the step → how-to → Way of Selling → shelf & side cards */}
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={trackBarStyle}>
            <SectionLabel style={{ marginBottom: 0, marginRight: 4 }}>Role tracks — one per population</SectionLabel>
            {tracks.map(t => (
              <button key={t.id} onClick={() => setTrackId(t.id)} style={{ ...tabStyle, ...(t.id === trackId ? tabOnStyle : {}) }}>
                {t.roleName}{t.population ? ` (${t.population})` : ''}
              </button>
            ))}
            <button onClick={addTrack} style={{ ...tabStyle, borderStyle: 'dashed', color: 'var(--ink-soft)' }}>+ track</button>
            <button
              onClick={toggleCompass}
              title="HB Compass sharpens the proposal with a customer-specific Ideal Profile — recommended, not required. Reversible until Gate 2."
              aria-pressed={compass}
              style={{ ...compassToggleStyle, ...(compass ? {} : { color: 'var(--danger, #c8102e)' }), ...(compassLocked ? { opacity: 0.6 } : {}) }}
            >
              {compass
                ? <CheckCircleIcon style={{ width: 16, height: 16 }} />
                : <span style={{ width: 13, height: 13, borderRadius: 3, border: '1.5px solid currentColor', display: 'inline-block' }} />}
              Adapt with HB Compass
            </button>
          </div>

          {/* The step — the work itself comes first */}
          {embedded ? <div style={{ marginBottom: 14 }}>{step}</div> : <Card style={{ padding: '22px 28px', marginBottom: 14 }}>{step}</Card>}

          {/* How to — what this phase is for, what to do in this step, the tools for it, how you move on */}
          <div style={howToStyle}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <span style={howToLabelStyle}>How to · {ph.label} · step {stageIdx + 1} of {stages.length}</span>
              {isProfileStage && <span style={optionalPillStyle}>HB Compass profile optional</span>}
            </div>
            <p style={{ margin: '0 0 14px', fontSize: '0.9rem', lineHeight: 1.6, color: 'var(--body-ink)' }}>{ph.howTo}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 18, fontSize: '0.82rem', borderTop: '1px solid var(--line)', paddingTop: 14 }}>
              <div>
                <div style={howToHeadStyle}>What you do in “{sel.name}”</div>
                {milestones.length ? (
                  <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {milestones.map(m => (
                      <li key={m.id} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', color: m.done ? 'var(--ink-faint)' : 'var(--ink)' }}>
                        <span style={{ flexShrink: 0 }}>{m.done ? '✓' : '○'}</span>
                        <span style={{ textDecoration: m.done ? 'line-through' : 'none' }}>{m.label}</span>
                      </li>
                    ))}
                  </ul>
                ) : <div style={{ color: 'var(--ink-faint)' }}>{sel.entryCriterion ? `You are here once: ${sel.entryCriterion.toLowerCase()}.` : 'No checklist for this step yet.'}</div>}
              </div>
              <div>
                <div style={howToHeadStyle}>Tools for this step</div>
                {kit.length || sel.primaryActionRoute ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {kit.map(k => k.route
                      ? <Link key={k.id} to={k.route} style={toolLinkStyle}>{k.label} ↗</Link>
                      : <span key={k.id}>{k.label}</span>)}
                    {sel.primaryActionRoute && <Link to={sel.primaryActionRoute} style={toolLinkStyle}>{sel.primaryActionLabel || `Open ${ph.label}`} ↗</Link>}
                  </div>
                ) : <div style={{ color: 'var(--ink-faint)' }}>Everything lives on the {ph.label} shelf.</div>}
              </div>
              <div>
                <div style={howToHeadStyle}>How you move on</div>
                {nextStage
                  ? <div>Next: <b>{nextStage.name}</b>{nextStage.entryCriterion ? <> — {/^(gate|won)\b/i.test(nextStage.entryCriterion) ? `after ${nextStage.entryCriterion.replace(/ — /, ': ')}` : `when ${nextStage.entryCriterion.charAt(0).toLowerCase() + nextStage.entryCriterion.slice(1)}`}.</> : '.'}{GATE_BEFORE[seqAt(stageIdx + 1)] && <div style={{ color: 'rgba(200,16,46,0.8)', marginTop: 3 }}>🔒 {GATE_BEFORE[seqAt(stageIdx + 1)]}</div>}</div>
                  : <div>This is the last step — the journey continues in <b>Explore</b> with the customer’s next need.</div>}
              </div>
            </div>
          </div>

          {wos && <WayOfSelling key={`wos-${railRev}`} projectId={projectId} wos={wos} lostReasons={rail.lostReasons} onChanged={refresh} />}

          <div style={shelfRowStyle}>
            <span>Everything for this phase — knowledge, method, materials — lives on the <b>{ph.label} shelf</b>.</span>
            <Btn size="sm" variant="outline" onClick={() => navigate(ph.shelf)} style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>Open the shelf ↗</Btn>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <SideCard title="✦ Companion">
              I know where this stands — ask me anything about {custName || 'this customer'}{/\.$/.test(custName) ? '' : '.'}{' '}
              <Btn variant="link" onClick={() => window.dispatchEvent(new CustomEvent('hb-assistant-open', { detail: { prompt: `Where does ${projName}${custName ? ` (${custName})` : ''} stand, and what should my next step be?` } }))}
                style={{ fontSize: 'inherit', display: 'inline' }}>Ask the HB Assistant →</Btn>
            </SideCard>
            <SideCard title="Documents">intake notes · attached files · proposal artefacts land here</SideCard>
            <SideCard title="Parties & activity">{(project.parties || []).map(p => `${p.role}: ${p.name}`).join(' · ') || 'sponsor, partners, participants'}{project.nextStep ? ` · next: ${project.nextStep}` : ''}</SideCard>
          </div>
          {project.companyId && <div style={{ marginTop: 14 }}><LatestFacts companyId={project.companyId} name={custName} /></div>}
        </div>
      </div>
    </>
  );
}

// Stage status indicator — done (check), current (filled dot), future (hollow dot).
function StageDot({ done, current, selected }) {
  const wrap = { width: 17, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 };
  if (done) return <span style={wrap}><CheckCircleIcon style={{ width: 16, height: 16, color: selected ? '#fff' : 'var(--ink-soft)' }} /></span>;
  const dot = { width: 9, height: 9, borderRadius: '50%' };
  if (current) return <span style={wrap}><span style={{ ...dot, background: selected ? '#fff' : 'var(--accent, #c8102e)' }} /></span>;
  return <span style={wrap}><span style={{ ...dot, border: `1.5px solid ${selected ? 'rgba(255,255,255,0.6)' : 'var(--ink-faint)'}` }} /></span>;
}

function SideCard({ title, children }) {
  return (
    <div style={{ border: 'var(--panel-border)', borderRadius: 'var(--radius-panel)', padding: '18px 22px', fontSize: '0.85rem', background: 'var(--canvas-white)' }}>
      <div style={{ ...howToLabelStyle, marginBottom: 8 }}>{title}</div>
      <div style={{ color: 'var(--body-ink)', lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}

// ── styles ──────────────────────────────────────────────────────────────────
// Phase header = clickable shelf link, styled exactly like the app's AreaLayout section headers.
const phHeadStyle = { display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', padding: '12px 10px 4px' };
const gateStyle = { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.64rem', color: 'rgba(200,16,46,0.8)', fontWeight: 600, padding: '4px 10px 4px 12px', letterSpacing: '0.02em' };
const navItemStyle = { display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', fontSize: '0.85rem', padding: '8px 10px', borderRadius: 'var(--radius-md)', marginBottom: 2, cursor: 'pointer', lineHeight: 1.3, border: 'none', fontFamily: 'var(--font-body)' };
const tabStyle = { fontSize: '0.82rem', fontWeight: 700, border: '1px solid var(--line)', borderRadius: 999, padding: '6px 15px', cursor: 'pointer', background: 'var(--canvas-white)', color: 'var(--body-ink)', fontFamily: 'var(--font-body)' };
const journeyToggleStyle = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px', border: 'var(--panel-border)', borderRadius: 'var(--radius-panel)', background: 'var(--canvas-white)', cursor: 'pointer', fontFamily: 'var(--font-body)', color: 'var(--ink)' };
const tabOnStyle = { background: 'var(--ink)', color: '#fff', borderColor: 'var(--ink)' };
const trackBarStyle = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14, padding: '14px 20px', background: 'var(--canvas-white)', border: 'var(--panel-border)', borderRadius: 'var(--radius-panel)' };
const compassToggleStyle = { marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: '0.82rem', fontWeight: 600, padding: '5px 4px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink)', fontFamily: 'var(--font-body)' };
const shelfRowStyle = { background: 'var(--canvas-white)', border: 'var(--panel-border)', borderRadius: 'var(--radius-panel)', padding: '14px 22px', fontSize: '0.86rem', color: 'var(--body-ink)', margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 10 };
const howToStyle = { border: 'var(--panel-border)', borderRadius: 'var(--radius-panel)', padding: '22px 28px', marginBottom: 14, background: 'var(--canvas-white)' };
const howToLabelStyle = { fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)' };
const howToHeadStyle = { fontSize: '0.78rem', fontWeight: 700, color: 'var(--ink)', marginBottom: 6 };
const optionalPillStyle = { fontSize: '0.64rem', padding: '1px 8px', borderRadius: 99, border: '1px dashed var(--ink-faint)', color: 'var(--ink-soft)' };
const toolLinkStyle = { color: 'var(--ink)', textDecoration: 'underline', textUnderlineOffset: 2 };
const stepNoteStyle = { fontSize: '0.9rem', color: 'var(--ink-soft)', lineHeight: 1.6, marginTop: 0 };
const stepTitleStyle = { fontFamily: 'var(--font-display)', fontSize: '1.4rem', marginBottom: 10 };
